import { useTranslation } from 'react-i18next';
import { FileVideo, Link2, PlayCircle, VideoOff } from 'lucide-react';
import type {
  AssistantCoursePlan,
  AssistantCoursePlanLesson,
} from '@xtreed/academy-shared';
import { cn } from '@/lib/utils';
import { Badge } from '@xtreed/academy-shared/ui';

export type CoursePlanArgs = AssistantCoursePlan & {
  productId?: string | null;
};

interface CoursePlanCardProps {
  plan: CoursePlanArgs;
  editable: boolean;
  onChange: (plan: CoursePlanArgs) => void;
}

function EditableText({
  value,
  editable,
  onChange,
  className,
}: {
  value: string;
  editable: boolean;
  onChange: (value: string) => void;
  className?: string;
}) {
  if (!editable) return <span className={className}>{value}</span>;
  return (
    <input
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={cn(
        'w-full min-w-0 rounded-md border border-transparent bg-transparent px-1 py-0.5 outline-hidden transition-colors hover:border-border/60 focus:border-primary/60 focus:bg-background/60',
        className,
      )}
    />
  );
}

function LessonVideoIcon({ lesson }: { lesson: AssistantCoursePlanLesson }) {
  const { t } = useTranslation('adminAssistant');
  if (lesson.video?.uploadId) {
    return (
      <FileVideo
        className="h-3.5 w-3.5 shrink-0 text-primary-ink"
        aria-label={t('plan.videoUpload')}
      />
    );
  }
  if (lesson.video?.url) {
    return (
      <Link2
        className="h-3.5 w-3.5 shrink-0 text-primary-ink"
        aria-label={t('plan.videoLink')}
      />
    );
  }
  if (!lesson.type || lesson.type === 'VIDEO') {
    return (
      <VideoOff
        className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60"
        aria-label={t('plan.noVideo')}
      />
    );
  }
  return <PlayCircle className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />;
}

export function CoursePlanCard({
  plan,
  editable,
  onChange,
}: CoursePlanCardProps) {
  const { t } = useTranslation('adminAssistant');
  const lessonCount = plan.modules.reduce(
    (sum, module) => sum + module.lessons.length,
    0,
  );

  const updateModule = (index: number, title: string) =>
    onChange({
      ...plan,
      modules: plan.modules.map((module, current) =>
        current === index ? { ...module, title } : module,
      ),
    });

  const updateLesson = (
    moduleIndex: number,
    lessonIndex: number,
    title: string,
  ) =>
    onChange({
      ...plan,
      modules: plan.modules.map((module, current) =>
        current === moduleIndex
          ? {
              ...module,
              lessons: module.lessons.map((lesson, index) =>
                index === lessonIndex ? { ...lesson, title } : lesson,
              ),
            }
          : module,
      ),
    });

  return (
    <div className="space-y-3">
      <div>
        <p className="text-[10px] font-semibold tracking-wider text-primary-ink uppercase">
          {plan.productId ? t('plan.existingCourse') : t('plan.title')}
        </p>
        <EditableText
          value={plan.title}
          editable={editable && !plan.productId}
          onChange={(title) => onChange({ ...plan, title })}
          className="mt-1 block text-base font-semibold text-foreground"
        />
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          <Badge variant="secondary" className="text-[10px]">
            {t('plan.modules', { count: plan.modules.length })}
          </Badge>
          <Badge variant="secondary" className="text-[10px]">
            {t('plan.lessons', { count: lessonCount })}
          </Badge>
          {plan.visibility && (
            <Badge variant="outline" className="text-[10px]">
              {t(`plan.visibility.${plan.visibility}`)}
            </Badge>
          )}
        </div>
      </div>

      <ol className="space-y-2">
        {plan.modules.map((module, moduleIndex) => (
          <li
            key={moduleIndex}
            className="rounded-xl border border-border/50 bg-background/40 p-2.5"
          >
            <div className="flex items-center gap-2">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-primary/15 text-[10px] font-bold text-primary-ink">
                {moduleIndex + 1}
              </span>
              <EditableText
                value={module.title}
                editable={editable}
                onChange={(title) => updateModule(moduleIndex, title)}
                className="text-sm font-medium text-foreground"
              />
            </div>
            <ol className="mt-1.5 space-y-0.5 pl-7">
              {module.lessons.map((lesson, lessonIndex) => (
                <li
                  key={lessonIndex}
                  className="flex items-center gap-2 text-xs text-muted-foreground"
                >
                  <LessonVideoIcon lesson={lesson} />
                  <EditableText
                    value={lesson.title}
                    editable={editable}
                    onChange={(title) =>
                      updateLesson(moduleIndex, lessonIndex, title)
                    }
                    className="text-xs"
                  />
                  {lesson.isFree && (
                    <Badge variant="outline" className="shrink-0 text-[9px]">
                      {t('plan.free')}
                    </Badge>
                  )}
                </li>
              ))}
            </ol>
          </li>
        ))}
      </ol>

      {editable && (
        <p className="text-[11px] text-muted-foreground">
          {t('plan.editHint')}
        </p>
      )}
    </div>
  );
}
